import {
  type AxiosRequestConfig,
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  AxiosHeaders,
} from 'axios'
import QueryString from 'query-string'

import {
  HttpError,
  type HttpResponse,
  type IHttpClient,
  type RequestConfig,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'

export class AxiosHttpClient implements IHttpClient {
  constructor(private client: AxiosInstance) {
    const errorInterceptor = (error: AxiosError) => {
      // Use AxiosHeaders.toJSON(true) to convert all values to strings
      const headers = error.response?.headers
      const normalizedHeaders =
        headers instanceof AxiosHeaders ? headers.toJSON(true) : headers || {}

      throw new HttpError(
        error.response?.status || -1,
        error.response?.data || null,
        normalizedHeaders as Record<string, string | undefined>
      )
    }

    client.interceptors.response.use(undefined, errorInterceptor)
  }

  private async transformResponse<T>(
    promise: Promise<AxiosResponse<T>>
  ): Promise<HttpResponse<T>> {
    const response = await promise
    const headers = response.headers
    const normalizedHeaders =
      headers instanceof AxiosHeaders ? headers.toJSON(true) : headers

    return {
      data: response.data,
      status: response.status,
      headers: normalizedHeaders as Record<string, string | undefined>,
    }
  }

  extendConfig = (config: RequestConfig = {}): AxiosRequestConfig => {
    const { arrayParamFormat = 'bracket', ...axiosRequestConfig } = config || {}

    return {
      ...axiosRequestConfig,
      paramsSerializer: (paramObj: RequestConfig['params']) => {
        /**
         * This is the default param serializer axios uses
         */
        return QueryString.stringify(paramObj, {
          arrayFormat: arrayParamFormat,
        })
      },
    }
  }

  get<T = any, D = any>(
    url: string,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(
      this.client.get(url, this.extendConfig(config))
    )
  }

  delete<T = any, D = any>(
    url: string,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(this.client.delete(url, config))
  }

  head<T = any, D = any>(
    url: string,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(this.client.head(url, config))
  }

  options<T = any, D = any>(
    url: string,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(this.client.options(url, config))
  }

  post<T = any, D = any>(
    url: string,
    data?: D,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(this.client.post(url, data, config))
  }

  put<T = any, D = any>(
    url: string,
    data?: D,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(this.client.put(url, data, config))
  }

  patch<T = any, D = any>(
    url: string,
    data?: D,
    config?: RequestConfig<D>
  ): Promise<HttpResponse<T>> {
    return this.transformResponse(this.client.patch(url, data, config))
  }
}
